/**
 * Advanced Data Table Component
 * Smart filtering, bulk operations, inline editing, and export capabilities
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Checkbox,
  IconButton,
  Toolbar,
  Typography,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  LinearProgress,
  Collapse,
  Badge,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  Delete,
  FilterList,
  GetApp,
  Print,
  Search,
  Clear,
  Edit,
  Save,
  Cancel,
  MoreVert,
  ViewColumn,
  Download,
  Upload,
  ContentCopy,
  SelectAll,
  Refresh,
  Settings,
  SaveAlt,
  PictureAsPdf,
  TableChart,
  Add,
  Remove,
  FilterAlt,
  Sort,
} from '@mui/icons-material';
// Note: CSV/Excel export temporarily disabled due to import issues
// Will be re-enabled after library dependencies are verified

export interface Column<T = any> {
  id: string;
  label: string;
  dataKey: keyof T | ((row: T) => any);
  width?: number;
  align?: 'left' | 'center' | 'right';
  format?: (value: any) => string | React.ReactNode;
  renderCell?: (row: T) => React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  editable?: boolean;
  editType?: 'text' | 'number' | 'select' | 'date';
  editOptions?: { value: any; label: string }[];
  validation?: (value: any) => boolean | string;
  hidden?: boolean;
  sticky?: boolean;
}

export interface FilterConfig {
  column: string;
  operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'gt' | 'lt' | 'between';
  value: any;
  value2?: any; // For 'between' operator
}

export interface AdvancedDataTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  title?: string;
  keyField: keyof T;
  onRowClick?: (row: T) => void;
  onRowEdit?: (row: T) => void;
  onBulkDelete?: (selectedRows: T[]) => void;
  onBulkUpdate?: (selectedRows: T[], updates: Partial<T>) => void;
  onRefresh?: () => void;
  enableColumnVisibility?: boolean;
  enableFiltering?: boolean;
  enableSorting?: boolean;
  enableSelection?: boolean;
  enableInlineEdit?: boolean;
  enableExport?: boolean;
  enablePagination?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  customActions?: {
    label: string;
    icon: React.ReactNode;
    action: (selectedRows: T[]) => void;
    disabled?: (selectedRows: T[]) => boolean;
    requiresSelection?: boolean; // New prop to control when action is shown
  }[];
  stickyHeader?: boolean;
  maxHeight?: string | number;
}

export function AdvancedDataTable<T extends Record<string, any>>({
  columns: initialColumns,
  data,
  loading = false,
  title,
  keyField,
  onRowClick,
  onRowEdit,
  onBulkDelete,
  onBulkUpdate,
  onRefresh,
  enableColumnVisibility = true,
  enableFiltering = true,
  enableSorting = true,
  enableSelection = true,
  enableInlineEdit = true,
  enableExport = true,
  enablePagination = true,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  customActions = [],
  stickyHeader = true,
  maxHeight = 600,
}: AdvancedDataTableProps<T>) {
  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize);
  const [selected, setSelected] = useState<T[keyof T][]>([]);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('');
  const [filters, setFilters] = useState<FilterConfig[]>([]);
  const [globalSearch, setGlobalSearch] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    initialColumns.map(col => col.id) // Show ALL columns by default
  );
  const [editingCell, setEditingCell] = useState<{ rowId: any; columnId: string } | null>(null);
  const [editValue, setEditValue] = useState<any>('');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<HTMLElement | null>(null);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [savedFilters, setSavedFilters] = useState<{ name: string; filters: FilterConfig[] }[]>([]);
  const [filterForm, setFilterForm] = useState({
    column: '',
    operator: 'contains' as FilterConfig['operator'],
    value: ''
  });
  
  // Refs
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Memoized columns
  const columns = useMemo(
    () => initialColumns.filter(col => visibleColumns.includes(col.id)),
    [initialColumns, visibleColumns]
  );

  // Data filtering and sorting
  const processedData = useMemo(() => {
    let filtered = [...data];

    // Global search
    if (globalSearch) {
      filtered = filtered.filter(row =>
        columns.some(col => {
          const value = typeof col.dataKey === 'function' 
            ? col.dataKey(row) 
            : row[col.dataKey as keyof T];
          return String(value).toLowerCase().includes(globalSearch.toLowerCase());
        })
      );
    }

    // Column filters
    filters.forEach(filter => {
      const column = initialColumns.find(col => col.id === filter.column);
      if (!column) return;

      const beforeFilterCount = filtered.length;

      filtered = filtered.filter(row => {
        const value = typeof column.dataKey === 'function'
          ? column.dataKey(row)
          : row[column.dataKey as keyof T];
        
        // Skip rows with null/undefined values for most operators
        if (value === null || value === undefined) {
          return false;
        }
        
        switch (filter.operator) {
          case 'equals':
            // Handle both string and numeric equality
            return String(value) === String(filter.value) || Number(value) === Number(filter.value);
          case 'contains':
            // Skip N/A values for contains filter
            if (String(value) === 'N/A') {
              return false;
            }
            
            // For numeric values, also check if the filter value equals the row value
            const stringValue = String(value).toLowerCase();
            const stringFilter = String(filter.value).toLowerCase();
            
            // If both are numeric, also check for exact equality
            if (!isNaN(Number(value)) && !isNaN(Number(filter.value))) {
              return stringValue.includes(stringFilter) || Number(value) === Number(filter.value);
            }
            
            return stringValue.includes(stringFilter);
          case 'startsWith':
            return String(value).toLowerCase().startsWith(String(filter.value).toLowerCase());
          case 'endsWith':
            return String(value).toLowerCase().endsWith(String(filter.value).toLowerCase());
          case 'gt':
            return Number(value) > Number(filter.value);
          case 'lt':
            return Number(value) < Number(filter.value);
          case 'between':
            return Number(value) >= Number(filter.value) && Number(value) <= Number(filter.value2);
          default:
            return true;
        }
      });
      
      console.log('🔍 Filter applied:', { 
        beforeCount: beforeFilterCount, 
        afterCount: filtered.length, 
        filter: filter.column + ' ' + filter.operator + ' ' + filter.value 
      });
    });

    // Sorting
    if (orderBy) {
      const column = columns.find(col => col.id === orderBy);
      if (column) {
        filtered.sort((a, b) => {
          const aValue = typeof column.dataKey === 'function'
            ? column.dataKey(a)
            : a[column.dataKey as keyof T];
          const bValue = typeof column.dataKey === 'function'
            ? column.dataKey(b)
            : b[column.dataKey as keyof T];
          
          if (aValue < bValue) return order === 'asc' ? -1 : 1;
          if (aValue > bValue) return order === 'asc' ? 1 : -1;
          return 0;
        });
      }
    }

    return filtered;
  }, [data, globalSearch, filters, orderBy, order, columns, initialColumns]);

  // Pagination
  const paginatedData = useMemo(() => {
    if (!enablePagination) return processedData;
    const start = page * rowsPerPage;
    return processedData.slice(start, start + rowsPerPage);
  }, [processedData, page, rowsPerPage, enablePagination]);

  // Selection handlers
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = paginatedData.map(row => row[keyField]);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectClick = (event: React.MouseEvent, id: T[keyof T]) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected: T[keyof T][] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const getSelectedRows = (): T[] => {
    return data.filter(row => selected.includes(row[keyField]));
  };

  // Sorting handlers
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Inline editing handlers
  const handleEditClick = (rowId: any, columnId: string, currentValue: any) => {
    setEditingCell({ rowId, columnId });
    setEditValue(currentValue);
  };

  const handleEditSave = () => {
    if (!editingCell) return;

    const column = columns.find(col => col.id === editingCell.columnId);
    if (!column) return;

    // Validation
    if (column.validation) {
      const validationResult = column.validation(editValue);
      if (typeof validationResult === 'string') {
        setSnackbar({ open: true, message: validationResult, severity: 'error' });
        return;
      }
      if (!validationResult) {
        setSnackbar({ open: true, message: 'Invalid value', severity: 'error' });
        return;
      }
    }

    const row = data.find(r => r[keyField] === editingCell.rowId);
    if (row && onRowEdit) {
      const updatedRow = { ...row };
      if (typeof column.dataKey === 'string') {
        updatedRow[column.dataKey] = editValue;
      }
      onRowEdit(updatedRow);
    }

    setEditingCell(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Filter handlers
  const addFilter = (filter: FilterConfig) => {
    setFilters([...filters, filter]);
  };

  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const clearFilters = () => {
    setFilters([]);
    setGlobalSearch('');
  };

  // Export handlers - temporarily disabled due to library import issues
  const exportToCSV = () => {
    // TODO: Re-enable after fixing react-csv import
    setSnackbar({ open: true, message: 'CSV export temporarily disabled', severity: 'info' });
  };

  const exportToExcel = () => {
    // TODO: Re-enable after fixing xlsx import
    setSnackbar({ open: true, message: 'Excel export temporarily disabled', severity: 'info' });
  };

  const exportToPDF = () => {
    // TODO: Re-enable after fixing jspdf import
    setSnackbar({ open: true, message: 'PDF export temporarily disabled', severity: 'info' });
  };

  // Column visibility handler
  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  // Render cell content
  const renderCellContent = (row: T, column: Column<T>) => {
    const value = typeof column.dataKey === 'function'
      ? column.dataKey(row)
      : row[column.dataKey as keyof T];
    
    const isEditing = editingCell?.rowId === row[keyField] && editingCell?.columnId === column.id;
    
    if (isEditing && column.editable) {
      switch (column.editType) {
        case 'select':
          return (
            <FormControl size="small" fullWidth>
              <Select
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleEditSave();
                  if (e.key === 'Escape') handleEditCancel();
                }}
                autoFocus
              >
                {column.editOptions?.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        
        case 'number':
          return (
            <TextField
              size="small"
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(Number(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEditSave();
                if (e.key === 'Escape') handleEditCancel();
              }}
              autoFocus
              fullWidth
            />
          );
        
        case 'date':
          return (
            <TextField
              size="small"
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEditSave();
                if (e.key === 'Escape') handleEditCancel();
              }}
              autoFocus
              fullWidth
            />
          );
        
        default:
          return (
            <TextField
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEditSave();
                if (e.key === 'Escape') handleEditCancel();
              }}
              autoFocus
              fullWidth
            />
          );
      }
    }
    
    // Check for renderCell first, then format, then raw value
    if (column.renderCell) {
      return column.renderCell(row);
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {column.format ? column.format(value) : value}
        {column.editable && enableInlineEdit && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row[keyField], column.id, value);
            }}
            sx={{ opacity: 0, transition: 'opacity 0.2s' }}
            className="edit-button"
          >
            <Edit fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Paper elevation={2} sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Toolbar */}
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          minHeight: '40px !important',
          height: 40,
          ...(selected.length > 0 && {
            bgcolor: (theme) => theme.palette.action.activatedOpacity,
          }),
        }}
      >
        {selected.length > 0 ? (
          <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1">
            {selected.length} selected
          </Typography>
        ) : (
          <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle">
            {title}
          </Typography>
        )}

        {/* Global Search */}
        {enableFiltering && selected.length === 0 && (
          <TextField
            size="small"
            placeholder="Search..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: globalSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setGlobalSearch('')}>
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ 
              mr: 2, 
              minWidth: 200,
              '& .MuiInputBase-root': {
                height: 32,
                fontSize: 'var(--table-font-size-dense)'
              }
            }}
          />
        )}

        {/* Action Buttons */}
        {selected.length > 0 ? (
          <>
            {customActions.filter(action => action.requiresSelection !== false).map((action, index) => (
              <Tooltip key={index} title={action.label}>
                <IconButton
                  onClick={() => action.action(getSelectedRows())}
                  disabled={action.disabled?.(getSelectedRows())}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}
            {onBulkUpdate && (
              <Tooltip title="Bulk Edit">
                <IconButton onClick={() => setBulkEditDialogOpen(true)}>
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {onBulkDelete && (
              <Tooltip title="Delete Selected">
                <IconButton onClick={() => onBulkDelete(getSelectedRows())}>
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            {/* Move filter button to the left side - first priority */}
            {enableFiltering && (
              <Badge badgeContent={filters.length} color="primary">
                <Tooltip title="Add Filters">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterList />}
                    onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                    sx={{ mr: 1, textTransform: 'none' }}
                  >
                    Filter {filters.length > 0 ? `(${filters.length})` : ''}
                  </Button>
                </Tooltip>
              </Badge>
            )}
            
            {/* Show Add actions as small text buttons */}
            {customActions.filter(action => action.requiresSelection === false && action.label.toLowerCase().includes('add')).map((action, index) => (
              <Button
                key={index}
                variant="text"
                onClick={() => action.action([])}
                disabled={action.disabled?.([])}
                size="small"
                sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', px: 1 }}
              >
                {action.label}
              </Button>
            ))}
            {/* Show Import/Export actions as icon buttons */}
            {customActions.filter(action => action.requiresSelection === false && !action.label.toLowerCase().includes('add')).map((action, index) => (
              <Tooltip key={index} title={action.label}>
                <IconButton
                  onClick={() => action.action([])}
                  disabled={action.disabled?.([])}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}
            
            {enableColumnVisibility && (
              <Tooltip title="Columns">
                <IconButton onClick={(e) => setColumnMenuAnchor(e.currentTarget)}>
                  <ViewColumn />
                </IconButton>
              </Tooltip>
            )}
            {onRefresh && (
              <Tooltip title="Refresh">
                <IconButton onClick={onRefresh}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Toolbar>

      {/* Active Filters Display */}
      {filters.length > 0 && (
        <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {filters.map((filter, index) => {
            const column = initialColumns.find(col => col.id === filter.column);
            return (
              <Chip
                key={index}
                label={`${column?.label}: ${filter.operator} ${filter.value}${filter.value2 ? ` - ${filter.value2}` : ''}`}
                onDelete={() => removeFilter(index)}
                size="small"
                color="primary"
                variant="outlined"
              />
            );
          })}
          <Button size="small" onClick={clearFilters}>
            Clear All
          </Button>
        </Box>
      )}

      {/* Loading Indicator */}
      {loading && <LinearProgress />}

      {/* Table */}
      <TableContainer ref={tableContainerRef} sx={{ 
        maxHeight,
        height: title?.includes('Data Management') ? 'var(--table-container-data-management)' : maxHeight
      }}>
        <Table stickyHeader={stickyHeader} size="small" sx={{ '& .MuiTableCell-root': { py: 'var(--table-cell-padding-ultra-dense)' } }}>
          <TableHead>
            <TableRow sx={{ 
              height: 'var(--table-header-height-ultra-dense)',
              '& .MuiTableCell-root': {
                fontSize: 'var(--table-font-size-ultra-dense)',
                fontWeight: 'var(--table-header-font-weight)',
                lineHeight: 'var(--table-line-height-ultra-dense)'
              }
            }}>
              {enableSelection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < paginatedData.length}
                    checked={paginatedData.length > 0 && selected.length === paginatedData.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.width }}
                  sx={{
                    ...(column.sticky && {
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'background.paper',
                      zIndex: 1,
                    }),
                  }}
                >
                  {column.sortable !== false && enableSorting ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => {
              const isItemSelected = selected.includes(row[keyField]);
              
              return (
                <TableRow
                  key={row[keyField]}
                  hover
                  onClick={(event) => {
                    // Only trigger row click if not clicking on checkbox or action buttons
                    if (onRowClick && !event.defaultPrevented) {
                      onRowClick(row);
                    }
                  }}
                  selected={isItemSelected}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    height: 'var(--table-row-height-ultra-dense)',
                    '&:hover': {
                      backgroundColor: 'var(--vrp-table-hover-bg)',
                    },
                    '&:hover .edit-button': {
                      opacity: 1,
                    },
                    '& .MuiTableCell-root': {
                      fontSize: 'var(--table-font-size-ultra-dense)',
                      lineHeight: 'var(--table-line-height-ultra-dense)',
                      padding: 'var(--table-cell-padding-ultra-dense) 8px'
                    },
                    ...(isItemSelected && {
                      backgroundColor: 'var(--vrp-table-selected-bg)',
                    }),
                  }}
                >
                  {enableSelection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={(event) => handleSelectClick(event, row[keyField])}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${row[keyField]}` }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        ...(column.sticky && {
                          position: 'sticky',
                          left: 0,
                          backgroundColor: 'background.paper',
                          zIndex: 1,
                        }),
                      }}
                    >
                      {renderCellContent(row, column)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + (enableSelection ? 1 : 0)} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {enablePagination && (
        <TablePagination
          rowsPerPageOptions={pageSizeOptions}
          component="div"
          count={processedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <Box sx={{ p: 2, minWidth: 350 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Add Filter
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Column</InputLabel>
            <Select
              value={filterForm.column}
              label="Column"
              onChange={(e) => setFilterForm({...filterForm, column: e.target.value})}
            >
              {initialColumns.filter(col => col.filterable).map(column => (
                <MenuItem key={column.id} value={column.id}>
                  {column.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Operator</InputLabel>
            <Select
              value={filterForm.operator}
              label="Operator"
              onChange={(e) => setFilterForm({...filterForm, operator: e.target.value as FilterConfig['operator']})}
            >
              <MenuItem value="contains">Contains</MenuItem>
              <MenuItem value="equals">Equals</MenuItem>
              <MenuItem value="startsWith">Starts With</MenuItem>
              <MenuItem value="endsWith">Ends With</MenuItem>
              <MenuItem value="gt">Greater Than</MenuItem>
              <MenuItem value="lt">Less Than</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Value"
            value={filterForm.value}
            onChange={(e) => setFilterForm({...filterForm, value: e.target.value})}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={() => {
                if (filterForm.column && filterForm.value) {
                  addFilter({
                    column: filterForm.column,
                    operator: filterForm.operator,
                    value: filterForm.value
                  });
                  setFilterForm({ column: '', operator: 'contains', value: '' });
                  setFilterMenuAnchor(null);
                }
              }}
              variant="contained"
              disabled={!filterForm.column || !filterForm.value}
            >
              Add Filter
            </Button>
            <Button 
              onClick={() => setFilterMenuAnchor(null)}
              variant="outlined"
            >
              Close
            </Button>
          </Box>
        </Box>
      </Menu>

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Show/Hide Columns
          </Typography>
          <List>
            {initialColumns.map((column) => (
              <ListItem key={column.id} dense>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visibleColumns.includes(column.id)}
                    onChange={() => handleColumnToggle(column.id)}
                  />
                </ListItemIcon>
                <ListItemText primary={column.label} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Menu>

      {/* Export Menu - temporarily simplified due to library import issues */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
      >
        <MenuItem onClick={exportToCSV}>
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          <ListItemText>Export as CSV (Coming Soon)</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToExcel}>
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          <ListItemText>Export as Excel (Coming Soon)</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToPDF}>
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          <ListItemText>Export as PDF (Coming Soon)</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}